import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import { useAuth } from '@/components/Auth/AuthContext';
import { getPrimaryRoleLabel } from '@/utils/swaRole';
import { azureSqlService } from '@/services/azureSqlService.js';
import CompanyAiNavigationTabs from './CompanyAiNavigationTabs';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  FormSection,
  Input,
  PageSection,
  SearchInput,
  Select,
  StatusBadge,
  Textarea,
} from '@/ui/components';
import {
  CompanyKnowledgeDocumentStructure,
  CompanyKnowledgeDocumentSummary,
  CompanyAiKnowledgeSource,
  CompanyAiKnowledgeSourceFile,
  CompanyAiModelSettings,
  CompanyAiPmQueryIntentProvider,
  CompanyAiPmQueryModelSize,
  CompanyAiSharePointBrowserResult,
  CompanyAiSharePointFolderFile,
  CompanyAiUsage,
  CompanyEmailDraftAssistance,
  CompanyEmailReviewDetail,
  CompanyEmailReviewListItem,
  CompanyEmailReviewStatus,
  companyAiService,
} from '@/services/companyAiService';
import { getVisibleProjectSuggestions } from './emailReviewProjectSuggestions';
import { EmailClassificationBadge, EmailReviewStatusBadge, formatEmailClassification } from './emailReviewPresentation';
import './companyAi.css';

const CUSTOM_QUOTA_EMAIL_OPTION = '__custom__';
const AI_ADMIN_USER_FALLBACKS = [
  { email: 'yl@linfinityeng.com', displayName: 'Yuan Lin' },
  { email: 'wd@linfinityeng.com', displayName: 'Wilson Du' },
  { email: 'jp@linfinityeng.com', displayName: 'JP' },
];
const KNOWLEDGE_SOURCE_CATEGORY_OPTIONS = [
  'Australian Standards',
  'Engineering References',
  'Templates',
  'Procedures',
  'Policies and Guides',
  'Client References',
  'Quotation References',
  'Checklists',
] as const;

type AiAdminUserOption = {
  userId: number | null;
  email: string;
  displayName: string;
  role: string | null;
  department: string | null;
  jobTitle: string | null;
  sourceTable: string | null;
};

type EmailTaskCreateDraft = {
  title: string;
  description: string;
  dueDate: string;
  priority: 'low' | 'medium' | 'high' | '';
};

type WorkflowHandoffDraft = CompanyEmailDraftAssistance['workflowHandoffs'][number];

type QuotationWorkflowPrefillState = {
  prefill: {
    entity?: string;
    clientName?: string;
    clientEmail?: string;
    projectDescription?: string;
    successMessage?: string;
  };
};

type InvoiceWorkflowPrefillState = {
  mode: 'create';
  prefill: {
    projectId?: string;
    clientName?: string;
    clientEmail?: string;
    dueDate?: string;
    notes?: string;
    items?: Array<{
      categoryName?: string;
      description: string;
      quantity: string;
      unitPrice: string;
      taxRate: string;
    }>;
    successMessage?: string;
  };
};

type LocalKnowledgeUploadEntry = {
  localId: string;
  file: File;
  relativePath: string;
  sizeBytes: number;
  contentType: string | null;
  isPdf: boolean;
  selected: boolean;
};

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

const EMAIL_REVIEW_TIMEZONE = 'Australia/Melbourne';

function formatDateTime(value?: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: EMAIL_REVIEW_TIMEZONE,
  });
}

function formatTokenCount(value: number) {
  return value.toLocaleString('en-AU');
}

function formatReasonCode(value: string) {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatReasonCodeList(values: string[]) {
  return values.map((value) => formatReasonCode(value)).join(', ');
}

function formatEvidenceSummary(evidence: Array<{ source: string; text: string }>) {
  return evidence
    .map((item) => String(item.text || '').trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(' · ');
}

function formatBehaviorDisposition(value?: string | null) {
  if (value === 'likely_reply') return 'Likely reply';
  if (value === 'likely_ignore') return 'Likely ignore';
  if (value === 'likely_action_only') return 'Likely action-only';
  return 'Undetermined';
}

function formatFileSize(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${value} B`;
}

function canUseSimplifiedKnowledgeEditor(source: CompanyAiKnowledgeSource) {
  return source.scopeType === 'company' && (source.sourceType === 'uploaded_folder' || source.sourceType === 'uploaded_file');
}

function formatKnowledgeSourceKind(sourceType: CompanyAiKnowledgeSource['sourceType']) {
  if (sourceType === 'uploaded_folder' || sourceType === 'uploaded_file') return 'Local upload';
  if (sourceType === 'sharepoint_folder') return 'SharePoint folder';
  if (sourceType === 'sharepoint_file') return 'SharePoint file';
  return sourceType.replace(/_/g, ' ');
}

function getKnowledgeIndexActionLabel(source: CompanyAiKnowledgeSource) {
  if (source.status === 'ready') return 'Rebuild Index';
  if (source.status === 'processing') return 'Building Index';
  return 'Build Index';
}

function buildKnowledgeSourceSelectLabel(
  source: CompanyAiKnowledgeSource,
  files: CompanyAiKnowledgeSourceFile[] | undefined,
) {
  const preferredFiles = (files || []).filter((file) => file.isSelected);
  const labelFiles = preferredFiles.length ? preferredFiles : (files || []);

  if (labelFiles.length === 1) {
    const fileLabel = labelFiles[0].relativePath || labelFiles[0].fileName;
    return `${source.aiKnowledgeSourceId} · ${fileLabel}`;
  }

  if (labelFiles.length > 1) {
    const firstFileLabel = labelFiles[0].relativePath || labelFiles[0].fileName;
    return `${source.aiKnowledgeSourceId} · ${firstFileLabel} + ${labelFiles.length - 1} more`;
  }

  return `${source.aiKnowledgeSourceId} · ${source.title} · ${source.pdfFileCount} PDF${source.pdfFileCount === 1 ? '' : 's'}`;
}

function buildEmailTaskCreateDraft(detail: CompanyEmailReviewDetail, suggestionId: number): EmailTaskCreateDraft | null {
  const suggestion = detail.taskSuggestions.find((item) => item.emailTaskSuggestionId === suggestionId);
  if (!suggestion) return null;
  return {
    title: suggestion.suggestedTitle || '',
    description: suggestion.suggestedDescription || suggestion.suggestedTitle || '',
    dueDate: suggestion.suggestedDueDate || '',
    priority: (suggestion.suggestedPriority || '') as EmailTaskCreateDraft['priority'],
  };
}

function readStringRecordValue(payload: Record<string, unknown>, key: string) {
  const value = payload[key];
  return typeof value === 'string' ? value.trim() : '';
}

function readNumberRecordValue(payload: Record<string, unknown>, key: string) {
  const value = Number(payload[key] || 0);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function buildTaskCreateDraftFromWorkflowHandoff(handoff: WorkflowHandoffDraft): { suggestionId: number; draft: EmailTaskCreateDraft } | null {
  if (handoff.handoffType !== 'task_draft') return null;
  const suggestionId = readNumberRecordValue(handoff.payload, 'emailTaskSuggestionId');
  if (!suggestionId) return null;
  const priority = readStringRecordValue(handoff.payload, 'priority').toLowerCase();
  return {
    suggestionId,
    draft: {
      title: readStringRecordValue(handoff.payload, 'title'),
      description: readStringRecordValue(handoff.payload, 'description'),
      dueDate: readStringRecordValue(handoff.payload, 'dueDate'),
      priority: priority === 'low' || priority === 'medium' || priority === 'high'
        ? priority
        : '',
    },
  };
}

function buildQuotationWorkflowStateFromWorkflowHandoff(handoff: WorkflowHandoffDraft, detail: CompanyEmailReviewDetail): QuotationWorkflowPrefillState {
  const clientName = readStringRecordValue(handoff.payload, 'clientName') || detail.message.fromName || '';
  const clientEmail = readStringRecordValue(handoff.payload, 'senderEmail') || detail.message.fromEmail || '';
  const projectDescription = readStringRecordValue(handoff.payload, 'subject')
    || detail.message.subject
    || handoff.title;
  return {
    prefill: {
      entity: 'Linfinity Engineering Pty Ltd',
      clientName,
      clientEmail,
      projectDescription,
      successMessage: `Loaded quotation follow-up draft from email ${detail.message.emailMessageId}. Review and complete the remaining quotation fields before creating.`,
    },
  };
}

function buildInvoiceWorkflowStateFromWorkflowHandoff(handoff: WorkflowHandoffDraft, detail: CompanyEmailReviewDetail): InvoiceWorkflowPrefillState {
  const clientName = readStringRecordValue(handoff.payload, 'clientName') || detail.message.fromName || '';
  const clientEmail = readStringRecordValue(handoff.payload, 'senderEmail') || detail.message.fromEmail || '';
  const dueDate = readStringRecordValue(handoff.payload, 'dueDate');
  const description = readStringRecordValue(handoff.payload, 'subject')
    || detail.message.subject
    || handoff.title;
  const projectId = readNumberRecordValue(handoff.payload, 'projectId');
  return {
    mode: 'create',
    prefill: {
      projectId: projectId ? String(projectId) : undefined,
      clientName,
      clientEmail,
      dueDate: dueDate || undefined,
      notes: readStringRecordValue(handoff.payload, 'summary') || detail.message.bodyPreview || undefined,
      items: [{
        categoryName: 'Email follow-up',
        description,
        quantity: '1',
        unitPrice: '0',
        taxRate: '0.1',
      }],
      successMessage: `Loaded invoice follow-up draft from email ${detail.message.emailMessageId}. Review the invoice details before saving or sending anything.`,
    },
  };
}

function getWorkflowHandoffActionLabel(handoff: WorkflowHandoffDraft): string | null {
  if (handoff.handoffType === 'task_draft') return 'Use in task create form';
  if (handoff.handoffType === 'quote_follow_up_draft') return 'Open quotation workflow';
  if (handoff.handoffType === 'invoice_follow_up_draft') return 'Open invoice workflow';
  return null;
}

function mergeAiAdminUserOptions(primary: AiAdminUserOption[], fallbacks: Array<{ email: string; displayName: string }>) {
  const merged = new Map<string, AiAdminUserOption>();

  primary.forEach((option) => {
    const normalizedEmail = String(option?.email || '').trim().toLowerCase();
    if (!normalizedEmail) return;

    merged.set(normalizedEmail, {
      ...option,
      email: normalizedEmail,
      displayName: String(option.displayName || normalizedEmail).trim() || normalizedEmail,
    });
  });

  fallbacks.forEach((fallback) => {
    const normalizedEmail = String(fallback?.email || '').trim().toLowerCase();
    if (!normalizedEmail || merged.has(normalizedEmail)) return;

    merged.set(normalizedEmail, {
      userId: null,
      email: normalizedEmail,
      displayName: String(fallback.displayName || normalizedEmail).trim() || normalizedEmail,
      role: null,
      department: null,
      jobTitle: null,
      sourceTable: 'ai_admin_fallback',
    });
  });

  return Array.from(merged.values()).sort((left, right) =>
    left.displayName.localeCompare(right.displayName, undefined, { sensitivity: 'base' })
  );
}

export default function AiAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeAdminTab, setActiveAdminTab] = useState('quota');
  const [quotas, setQuotas] = useState<CompanyAiUsage[]>([]);
  const [sources, setSources] = useState<CompanyAiKnowledgeSource[]>([]);
  const [modelSettings, setModelSettings] = useState<CompanyAiModelSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [quotaDataLoaded, setQuotaDataLoaded] = useState(false);
  const [knowledgeDataLoaded, setKnowledgeDataLoaded] = useState(false);
  const [modelDataLoaded, setModelDataLoaded] = useState(false);
  const [quotaUsers, setQuotaUsers] = useState<AiAdminUserOption[]>([]);
  const [quotaUsersBusy, setQuotaUsersBusy] = useState(false);
  const [quotaUsersMessage, setQuotaUsersMessage] = useState<string | null>(null);
  const [knowledgeDocuments, setKnowledgeDocuments] = useState<CompanyKnowledgeDocumentSummary[]>([]);
  const [selectedKnowledgeDocumentId, setSelectedKnowledgeDocumentId] = useState<number | null>(null);
  const [selectedKnowledgeDocumentStructure, setSelectedKnowledgeDocumentStructure] = useState<CompanyKnowledgeDocumentStructure | null>(null);
  const [knowledgeDocumentFilter, setKnowledgeDocumentFilter] = useState('');
  const [emailReviewList, setEmailReviewList] = useState<CompanyEmailReviewListItem[]>([]);
  const [emailReviewTotalCount, setEmailReviewTotalCount] = useState(0);
  const [selectedEmailReviewId, setSelectedEmailReviewId] = useState<number | null>(null);
  const [selectedEmailReviewDetail, setSelectedEmailReviewDetail] = useState<CompanyEmailReviewDetail | null>(null);
  const [emailReviewLoading, setEmailReviewLoading] = useState(false);
  const [emailReviewFeedback, setEmailReviewFeedback] = useState<string | null>(null);
  const [emailReviewFeedbackTone, setEmailReviewFeedbackTone] = useState<'error' | 'success'>('success');
  const [emailReviewClassification, setEmailReviewClassification] = useState('');
  const [emailReviewQueueMode, setEmailReviewQueueMode] = useState<'exceptions' | 'all'>('exceptions');
  const [emailReviewStatusFilter, setEmailReviewStatusFilter] = useState('');
  const [emailReviewLoadedFilterKey, setEmailReviewLoadedFilterKey] = useState('');
  const [openTaskCreateSuggestionId, setOpenTaskCreateSuggestionId] = useState<number | null>(null);
  const [emailTaskCreateDrafts, setEmailTaskCreateDrafts] = useState<Record<number, EmailTaskCreateDraft>>({});
  const [emailDraftAssistance, setEmailDraftAssistance] = useState<CompanyEmailDraftAssistance | null>(null);

  const [quotaEmail, setQuotaEmail] = useState('');
  const [quotaEmailMode, setQuotaEmailMode] = useState<'select' | 'custom'>('select');
  const [quotaLimit, setQuotaLimit] = useState('120000');
  const [quotaEnabled, setQuotaEnabled] = useState(true);
  const [quotaNotes, setQuotaNotes] = useState('');
  const [quotaFeedback, setQuotaFeedback] = useState<string | null>(null);
  const [quotaFeedbackTone, setQuotaFeedbackTone] = useState<'error' | 'success'>('success');
  const [savingQuota, setSavingQuota] = useState(false);
  const [pmQueryModelSize, setPmQueryModelSize] = useState<CompanyAiPmQueryModelSize>('mini');
  const [pmQueryIntentProvider, setPmQueryIntentProvider] = useState<CompanyAiPmQueryIntentProvider>('deterministic');
  const [modelFeedback, setModelFeedback] = useState<string | null>(null);
  const [modelFeedbackTone, setModelFeedbackTone] = useState<'error' | 'success'>('success');
  const [savingModelSettings, setSavingModelSettings] = useState(false);

  const [sourceType, setSourceType] = useState<'sharepoint_folder' | 'sharepoint_file' | 'uploaded_file' | 'uploaded_folder'>('uploaded_folder');
  const [scopeType, setScopeType] = useState('company');
  const [scopeRef, setScopeRef] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sharePointSiteId, setSharePointSiteId] = useState('');
  const [driveId, setDriveId] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [filePath, setFilePath] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [editingSourceId, setEditingSourceId] = useState<number | null>(null);
  const [sharePointBrowser, setSharePointBrowser] = useState<CompanyAiSharePointBrowserResult | null>(null);
  const [sharePointBrowserBusy, setSharePointBrowserBusy] = useState(false);
  const [sharePointFolderFiles, setSharePointFolderFiles] = useState<CompanyAiSharePointFolderFile[]>([]);
  const [sharePointFolderFilesBusy, setSharePointFolderFilesBusy] = useState(false);
  const [selectedSharePointFilePaths, setSelectedSharePointFilePaths] = useState<string[]>([]);
  const [selectedSourceFilesBySourceId, setSelectedSourceFilesBySourceId] = useState<Record<number, CompanyAiKnowledgeSourceFile[]>>({});
  const [localUploadEntries, setLocalUploadEntries] = useState<LocalKnowledgeUploadEntry[]>([]);
  const [sourceFeedback, setSourceFeedback] = useState<string | null>(null);
  const [sourceFeedbackTone, setSourceFeedbackTone] = useState<'error' | 'success'>('success');
  const [savingSource, setSavingSource] = useState(false);
  const [reindexingSourceId, setReindexingSourceId] = useState<number | null>(null);
  const [ingestingKnowledgeSourceId, setIngestingKnowledgeSourceId] = useState('');
  const [selectedPdfFileIds, setSelectedPdfFileIds] = useState<number[]>([]);
  const [knowledgeFeedback, setKnowledgeFeedback] = useState<string | null>(null);
  const [knowledgeFeedbackTone, setKnowledgeFeedbackTone] = useState<'error' | 'success'>('success');
  const [runningKnowledgeIngest, setRunningKnowledgeIngest] = useState(false);

  const emailReviewFilterKey = `${emailReviewClassification.trim().toLowerCase()}::${emailReviewQueueMode}::${emailReviewStatusFilter.trim().toLowerCase()}`;

  async function loadQuotaUsers() {
    setQuotaUsersBusy(true);
    try {
      const response = await azureSqlService.getWorkflowUserOptions();

      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];

      const normalized = rows
        .map((row: any) => {
          const email = String(row?.email || row?.Email || '').trim().toLowerCase();
          if (!email || !email.endsWith('@linfinityeng.com')) return null;
          return {
            userId: Number.isFinite(Number(row?.userId ?? row?.UserId)) ? Number(row?.userId ?? row?.UserId) : null,
            email,
            displayName: String(row?.displayName || row?.DisplayName || email).trim() || email,
            role: String(row?.role || row?.Role || '').trim() || null,
            department: String(row?.department || row?.Department || '').trim() || null,
            jobTitle: String(row?.jobTitle || row?.JobTitle || '').trim() || null,
            sourceTable: String(row?.sourceTable || row?.SourceTable || '').trim() || null,
          } as AiAdminUserOption;
        })
        .filter((row: AiAdminUserOption | null): row is AiAdminUserOption => Boolean(row));

      setQuotaUsers(mergeAiAdminUserOptions(normalized, AI_ADMIN_USER_FALLBACKS));
      setQuotaUsersMessage(
        normalized.length > 0
          ? null
          : 'Active users were limited in Users/UserList, so the internal team fallback is being shown.'
      );
    } catch (error) {
      setQuotaUsers(mergeAiAdminUserOptions([], AI_ADMIN_USER_FALLBACKS));
      setQuotaUsersMessage(
        error instanceof Error
          ? `${error.message}. Showing the internal team fallback instead.`
          : 'Active user lookup is unavailable, so the internal team fallback is being shown.'
      );
    } finally {
      setQuotaUsersBusy(false);
    }
  }

  async function refreshAdminData(
    scope: 'quota' | 'knowledge' | 'email' | 'model',
    options?: { showPageLoading?: boolean }
  ) {
    if (options?.showPageLoading) {
      setLoading(true);
    }

    try {
      if (scope === 'quota') {
        const [quotaResult] = await Promise.allSettled([
          companyAiService.listAdminQuotas(),
          loadQuotaUsers(),
        ]);

        if (quotaResult.status === 'fulfilled') {
          setQuotas(quotaResult.value);
          setQuotaDataLoaded(true);
        } else {
          setQuotaFeedback(normalizeErrorMessage(quotaResult.reason));
          setQuotaFeedbackTone('error');
        }
      }

      if (scope === 'knowledge') {
        const [sourcesResult, documentsResult] = await Promise.allSettled([
          companyAiService.listKnowledgeSources(),
          companyAiService.listKnowledgeDocuments(),
        ]);

        if (sourcesResult.status === 'fulfilled') {
          setSources(sourcesResult.value);
          setKnowledgeDataLoaded(true);
        } else {
          setSourceFeedback(normalizeErrorMessage(sourcesResult.reason));
          setSourceFeedbackTone('error');
        }

        if (documentsResult.status === 'fulfilled') {
          setKnowledgeDocuments(documentsResult.value);
          setKnowledgeDataLoaded(true);
        } else {
          setKnowledgeFeedback(normalizeErrorMessage(documentsResult.reason));
          setKnowledgeFeedbackTone('error');
        }
      }

      if (scope === 'model') {
        try {
          const settings = await companyAiService.getModelSettings();
          setModelSettings(settings);
          setPmQueryModelSize(settings.pmQueryModelSize);
          setPmQueryIntentProvider(settings.pmQueryIntentProvider || 'deterministic');
          setModelDataLoaded(true);
        } catch (error) {
          setModelFeedback(normalizeErrorMessage(error));
          setModelFeedbackTone('error');
        }
      }

      if (scope === 'email') {
        setEmailReviewLoading(true);
        try {
          const result = await companyAiService.listEmailReviewItems({
            page: 1,
            pageSize: 20,
            classification: emailReviewClassification || undefined,
            queueMode: emailReviewQueueMode,
            reviewStatus: emailReviewStatusFilter || undefined,
          });
          setEmailReviewList(result.items);
          setEmailReviewTotalCount(result.totalCount);
          setEmailReviewLoadedFilterKey(emailReviewFilterKey);
          if (selectedEmailReviewId && !result.items.some((item) => item.emailMessageId === selectedEmailReviewId)) {
            setSelectedEmailReviewId(null);
            setSelectedEmailReviewDetail(null);
          }
        } catch (error) {
          setEmailReviewFeedback(normalizeErrorMessage(error));
          setEmailReviewFeedbackTone('error');
        } finally {
          setEmailReviewLoading(false);
        }
      }
    } finally {
      if (options?.showPageLoading) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (activeAdminTab === 'quota' && !quotaDataLoaded) {
      void refreshAdminData('quota', { showPageLoading: true });
      return;
    }

    if (activeAdminTab === 'knowledge' && !knowledgeDataLoaded) {
      void refreshAdminData('knowledge', { showPageLoading: true });
      return;
    }

    if (activeAdminTab === 'model' && !modelDataLoaded) {
      void refreshAdminData('model', { showPageLoading: true });
      return;
    }

    if (activeAdminTab === 'email' && emailReviewLoadedFilterKey !== emailReviewFilterKey) {
      void refreshAdminData('email', { showPageLoading: true });
      return;
    }

    setLoading(false);
  }, [
    activeAdminTab,
    quotaDataLoaded,
    knowledgeDataLoaded,
    modelDataLoaded,
    emailReviewLoadedFilterKey,
    emailReviewFilterKey,
  ]);

  useEffect(() => {
    if (activeAdminTab !== 'knowledge') return;
    if (sourceType !== 'sharepoint_folder' && sourceType !== 'sharepoint_file') return;
    if (sharePointBrowser || sharePointBrowserBusy) return;
    void handleBrowseSharePoint(folderPath || undefined);
  }, [activeAdminTab, sourceType, sharePointBrowser, sharePointBrowserBusy, folderPath]);

  useEffect(() => {
    const sourceId = Number(ingestingKnowledgeSourceId || 0) || 0;
    if (sourceId <= 0) {
      setSelectedPdfFileIds([]);
      return;
    }

    void ensureSourceFilesLoaded(sourceId).then((files) => {
      const pdfIds = files.filter((file) => file.isPdf).map((file) => file.aiKnowledgeSourceFileId);
      setSelectedPdfFileIds(pdfIds);
    }).catch((error) => {
      setKnowledgeFeedback(normalizeErrorMessage(error));
      setKnowledgeFeedbackTone('error');
    });
  }, [ingestingKnowledgeSourceId]);

  useEffect(() => {
    if (activeAdminTab !== 'knowledge') return;

    const sourceIdsToLoad = sources
      .filter((source) => source.scopeType === 'company' && source.selectedFileCount > 0)
      .map((source) => source.aiKnowledgeSourceId)
      .filter((sourceId) => !selectedSourceFilesBySourceId[sourceId]?.length);

    if (!sourceIdsToLoad.length) return;

    void Promise.all(sourceIdsToLoad.map((sourceId) => ensureSourceFilesLoaded(sourceId))).catch(() => {
      // The selector can still fall back to the stored source title if file metadata is unavailable.
    });
  }, [activeAdminTab, sources, selectedSourceFilesBySourceId]);

  async function handleSaveQuota(event: React.FormEvent) {
    event.preventDefault();
    setSavingQuota(true);
    setQuotaFeedback(null);

    try {
      const normalizedQuotaEmail = quotaEmail.trim().toLowerCase();
      await companyAiService.upsertQuota({
        ownerEmail: normalizedQuotaEmail,
        monthlyTokenLimit: Number(quotaLimit),
        isEnabled: quotaEnabled,
        notes: quotaNotes.trim() || undefined,
      });
      setQuotaEmailMode('select');
      setQuotaEmail(normalizedQuotaEmail);
      setQuotaFeedback(`Updated quota for ${normalizedQuotaEmail}.`);
      setQuotaFeedbackTone('success');
      await refreshAdminData('quota');
    } catch (error) {
      setQuotaFeedback(normalizeErrorMessage(error));
      setQuotaFeedbackTone('error');
    } finally {
      setSavingQuota(false);
    }
  }

  async function handleSaveModelSettings(event: React.FormEvent) {
    event.preventDefault();
    setSavingModelSettings(true);
    setModelFeedback(null);

    try {
      const settings = await companyAiService.updateModelSettings({
        pmQueryModelSize,
        pmQueryIntentProvider,
      });
      setModelSettings(settings);
      setPmQueryModelSize(settings.pmQueryModelSize);
      setPmQueryIntentProvider(settings.pmQueryIntentProvider || 'deterministic');
      setModelDataLoaded(true);
      const parserOption = settings.intentProviderOptions?.find((option) => option.value === settings.pmQueryIntentProvider);
      setModelFeedback(`PM Query now uses ${settings.options.find((option) => option.value === settings.pmQueryModelSize)?.label || settings.pmQueryModelSize} (${settings.pmQueryModel}) with ${parserOption?.label || settings.pmQueryIntentProvider} intent parsing.`);
      setModelFeedbackTone('success');
    } catch (error) {
      setModelFeedback(normalizeErrorMessage(error));
      setModelFeedbackTone('error');
    } finally {
      setSavingModelSettings(false);
    }
  }

  function applyQuotaDraft(quota: CompanyAiUsage) {
    setQuotaEmailMode('select');
    setQuotaEmail(quota.ownerEmail);
    setQuotaLimit(String(quota.monthlyTokenLimit));
    setQuotaEnabled(quota.isEnabled);
    setQuotaNotes(quota.notes || '');
  }

  function resetQuotaDraft() {
    setQuotaLimit('120000');
    setQuotaEnabled(true);
    setQuotaNotes('');
  }

  function handleQuotaSelectionChange(value: string) {
    if (value === CUSTOM_QUOTA_EMAIL_OPTION) {
      setQuotaEmailMode('custom');
      setQuotaEmail('');
      resetQuotaDraft();
      return;
    }

    const matchedQuota = quotas.find((quota) => quota.ownerEmail.toLowerCase() === value.toLowerCase());
    if (matchedQuota) {
      applyQuotaDraft(matchedQuota);
      return;
    }

    setQuotaEmailMode('select');
    setQuotaEmail(value);
  }

  function resetKnowledgeSourceDraft() {
    setEditingSourceId(null);
    setSourceType('uploaded_folder');
    setScopeType('company');
    setScopeRef('');
    setTitle('');
    setDescription('');
    setSharePointSiteId('');
    setDriveId('');
    setFolderPath('');
    setFilePath('');
    setSourceUrl('');
    setSharePointBrowser(null);
    setSharePointFolderFiles([]);
    setSelectedSharePointFilePaths([]);
    setLocalUploadEntries([]);
  }

  async function ensureSourceFilesLoaded(sourceId: number) {
    if (selectedSourceFilesBySourceId[sourceId]?.length) {
      return selectedSourceFilesBySourceId[sourceId];
    }

    const files = await companyAiService.listKnowledgeSourceFiles(sourceId);
    setSelectedSourceFilesBySourceId((current) => ({
      ...current,
      [sourceId]: files,
    }));
    return files;
  }

  async function handleBrowseSharePoint(nextFolderPath?: string | null) {
    setSharePointBrowserBusy(true);
    setSourceFeedback(null);
    try {
      const result = await companyAiService.browseKnowledgeSharePoint({
        sharePointSiteId: sharePointSiteId.trim() || undefined,
        driveId: driveId.trim() || undefined,
        folderPath: (nextFolderPath ?? folderPath.trim()) || undefined,
      });
      setSharePointBrowser(result);
      setSharePointSiteId(result.sharePointSiteId || '');
      setDriveId(result.driveId || '');
    } catch (error) {
      setSourceFeedback(normalizeErrorMessage(error));
      setSourceFeedbackTone('error');
    } finally {
      setSharePointBrowserBusy(false);
    }
  }

  async function handleUseSharePointFolder(selectedFolderPath: string, selectedFolderUrl?: string | null) {
    setFolderPath(selectedFolderPath);
    setFilePath('');
    setSourceUrl(selectedFolderUrl || '');
    setSharePointFolderFilesBusy(true);
    setSourceFeedback(null);
    try {
      const files = await companyAiService.discoverKnowledgeSharePointFolderFiles({
        driveId: driveId.trim() || undefined,
        folderPath: selectedFolderPath,
      });
      setSharePointFolderFiles(files);
      setSelectedSharePointFilePaths(files.map((file) => file.sourceFilePath));
    } catch (error) {
      setSourceFeedback(normalizeErrorMessage(error));
      setSourceFeedbackTone('error');
    } finally {
      setSharePointFolderFilesBusy(false);
    }
  }

  function toggleSharePointFileSelection(filePathValue: string) {
    setSelectedSharePointFilePaths((current) => (
      current.includes(filePathValue)
        ? current.filter((value) => value !== filePathValue)
        : [...current, filePathValue]
    ));
  }

  function handleLocalUploadSelection(event: React.ChangeEvent<HTMLInputElement>, mode: 'uploaded_file' | 'uploaded_folder') {
    const files = Array.from(event.target.files || []);
    setLocalUploadEntries(files.map((file, index) => {
      const relativePathFromBrowser = mode === 'uploaded_folder'
        ? String((file as File & { webkitRelativePath?: string }).webkitRelativePath || '').trim()
        : '';
      const relativePath = relativePathFromBrowser || file.name || `file-${index + 1}`;
      return {
        localId: `${relativePath}-${index}`,
        file,
        relativePath,
        sizeBytes: file.size || 0,
        contentType: file.type || null,
        isPdf: /\.pdf$/i.test(file.name) || String(file.type || '').toLowerCase().includes('pdf'),
        selected: true,
      };
    }));
  }

  function toggleLocalUploadSelection(localId: string) {
    setLocalUploadEntries((current) => current.map((entry) => (
      entry.localId === localId
        ? { ...entry, selected: !entry.selected }
        : entry
    )));
  }

  async function handleEditSource(source: CompanyAiKnowledgeSource) {
    if (!canUseSimplifiedKnowledgeEditor(source)) {
      setSourceFeedback(`"${source.title}" uses the older ${formatKnowledgeSourceKind(source.sourceType)} flow. The simplified uploader only edits company-scoped local uploads.`);
      setSourceFeedbackTone('error');
      return;
    }

    setEditingSourceId(source.aiKnowledgeSourceId);
    setSourceType('uploaded_folder');
    setScopeType('company');
    setScopeRef('');
    setTitle(source.title || '');
    setDescription(source.description || '');
    setSharePointSiteId('');
    setDriveId('');
    setFolderPath('');
    setFilePath('');
    setSourceUrl('');
    setLocalUploadEntries([]);
    setSharePointBrowser(null);
    setSharePointFolderFiles([]);
    setSelectedSharePointFilePaths([]);
    setSourceFeedback(`Editing "${source.title}". Leave the upload box empty to keep the stored files, or pick new files to replace them before you build the index again.`);
    setSourceFeedbackTone('success');
  }

  async function handleReindexSource(sourceId: number) {
    setSourceFeedback(null);
    setReindexingSourceId(sourceId);
    try {
      await companyAiService.reindexKnowledgeSource(sourceId);
      setSourceFeedback(`Built the AI search index for source ${sourceId} from its saved KnowledgeBase files.`);
      setSourceFeedbackTone('success');
      await refreshAdminData('knowledge');
    } catch (error) {
      setSourceFeedback(normalizeErrorMessage(error));
      setSourceFeedbackTone('error');
    } finally {
      setReindexingSourceId((current) => (current === sourceId ? null : current));
    }
  }

  async function handleOpenSource(source: CompanyAiKnowledgeSource) {
    if (source.sourceUrl) {
      window.open(source.sourceUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const files = await ensureSourceFilesLoaded(source.aiKnowledgeSourceId);
    if (files[0]?.contentPath) {
      window.open(files[0].contentPath, '_blank', 'noopener,noreferrer');
      return;
    }

    setSourceFeedback(`No saved file URL is available yet for "${source.title}".`);
    setSourceFeedbackTone('error');
  }

  async function handleEnqueueSource(event: React.FormEvent) {
    event.preventDefault();
    setSavingSource(true);
    setSourceFeedback(null);

    try {
      const trimmedCategory = title.trim();
      if (!trimmedCategory) {
        throw new Error('Category is required.');
      }

      const selectedUploadedFiles = await Promise.all(selectedLocalUploadEntries.map(async (entry) => {
        const uploaded = await companyAiService.uploadAiFile(entry.file);
        return {
          draftFileId: uploaded.fileId,
          relativePath: entry.relativePath,
        };
      }));

      if (!selectedUploadedFiles.length && !editingSourceId) {
        throw new Error('Drop or choose at least one reference file before storing the source.');
      }

      const enqueueResult = await companyAiService.enqueueKnowledgeSource({
        aiKnowledgeSourceId: editingSourceId || undefined,
        sourceType: 'uploaded_folder',
        scopeType: 'company',
        title: trimmedCategory,
        description: description.trim() || undefined,
        selectedUploadedFiles,
        keepExistingFiles: Boolean(editingSourceId) && selectedUploadedFiles.length === 0,
        autoIndex: false,
      });

      const savedSourceId = Number((enqueueResult as any)?.aiKnowledgeSourceId || (enqueueResult as any)?.source?.aiKnowledgeSourceId || editingSourceId || 0);
      setSourceFeedback(
        `Stored ${selectedUploadedFiles.length || 0} file${selectedUploadedFiles.length === 1 ? '' : 's'} for "${trimmedCategory}" in KnowledgeBase. Build Index${savedSourceId ? ` on source ${savedSourceId}` : ''} when you're ready to make it searchable in AI.`
      );
      setSourceFeedbackTone('success');
      resetKnowledgeSourceDraft();
      await refreshAdminData('knowledge');
    } catch (error) {
      setSourceFeedback(normalizeErrorMessage(error));
      setSourceFeedbackTone('error');
    } finally {
      setSavingSource(false);
    }
  }

  async function handleIngestKnowledge(event: React.FormEvent) {
    event.preventDefault();
    const aiKnowledgeSourceId = Number(ingestingKnowledgeSourceId || 0) || 0;
    if (aiKnowledgeSourceId <= 0) {
      setKnowledgeFeedback('Choose a company knowledge source before running structured PDF ingestion.');
      setKnowledgeFeedbackTone('error');
      return;
    }
    if (!selectedPdfFileIds.length) {
      setKnowledgeFeedback('Pick at least one stored PDF from the selected source before running structured ingestion.');
      setKnowledgeFeedbackTone('error');
      return;
    }

    setRunningKnowledgeIngest(true);
    setKnowledgeFeedback(null);
    try {
      const result = await companyAiService.ingestKnowledgeSource({
        aiKnowledgeSourceId,
        aiKnowledgeSourceFileIds: selectedPdfFileIds,
      });
      setKnowledgeFeedback(`Built the page index for source ${aiKnowledgeSourceId}. ${result.documentCount} document${result.documentCount === 1 ? '' : 's'} extracted.`);
      setKnowledgeFeedbackTone('success');
      await refreshAdminData('knowledge');
    } catch (error) {
      setKnowledgeFeedback(normalizeErrorMessage(error));
      setKnowledgeFeedbackTone('error');
    } finally {
      setRunningKnowledgeIngest(false);
    }
  }

  async function handleSelectKnowledgeDocument(documentId: number) {
    setSelectedKnowledgeDocumentId(documentId);
    setSelectedKnowledgeDocumentStructure(await companyAiService.getKnowledgeDocumentStructure(documentId));
  }

  async function handleSelectEmailReview(emailMessageId: number) {
    setSelectedEmailReviewId(emailMessageId);
    setOpenTaskCreateSuggestionId(null);
    setEmailDraftAssistance(null);
    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);
    try {
      await reloadSelectedEmailReview(emailMessageId);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  async function reloadSelectedEmailReview(emailMessageId: number) {
    setSelectedEmailReviewDetail(await companyAiService.getEmailReviewItem(emailMessageId));
  }

  async function handleGenerateDraftAssistance() {
    if (!selectedEmailReviewId) return;
    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);
    try {
      setEmailDraftAssistance(await companyAiService.getEmailDraftAssistance(selectedEmailReviewId));
      setEmailReviewFeedback(`Draft assistance generated for email ${selectedEmailReviewId}.`);
      setEmailReviewFeedbackTone('success');
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  async function handleReviewEmailMessage(reviewStatus: CompanyEmailReviewStatus) {
    if (!selectedEmailReviewId) return;
    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);
    try {
      await companyAiService.reviewEmailMessage({
        emailMessageId: selectedEmailReviewId,
        reviewStatus,
      });
      setEmailReviewFeedback(
        reviewStatus === 'accepted'
          ? `Email ${selectedEmailReviewId} kept for PM follow-up review.`
          : `Email ${selectedEmailReviewId} ignored for PM follow-up review.`
      );
      setEmailReviewFeedbackTone('success');
      await refreshAdminData('email');
      await reloadSelectedEmailReview(selectedEmailReviewId);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  async function handleReviewSuggestion(
    kind: 'project' | 'task' | 'file',
    suggestionId: number,
    reviewStatus: CompanyEmailReviewStatus
  ) {
    if (!selectedEmailReviewId) return;
    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);
    try {
      await companyAiService.reviewEmailSuggestion({
        kind,
        suggestionId,
        reviewStatus,
      });
      setEmailReviewFeedback(
        kind === 'project' && reviewStatus === 'accepted'
          ? `Project suggestion ${suggestionId} kept. Other project matches for this email were ignored.`
          : `${kind} suggestion ${suggestionId} ${reviewStatus === 'accepted' ? 'kept' : 'dismissed'}.`
      );
      setEmailReviewFeedbackTone('success');
      await reloadSelectedEmailReview(selectedEmailReviewId);
      await refreshAdminData('email');
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  async function handleReprocessEmailMessage() {
    if (!selectedEmailReviewId) return;
    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);
    setOpenTaskCreateSuggestionId(null);
    setEmailDraftAssistance(null);
    try {
      const result = await companyAiService.reprocessEmailMessage(selectedEmailReviewId);
      setEmailReviewFeedback(
        `Reprocessed email ${selectedEmailReviewId}. Rebuilt ${result.projectSuggestionCount} project, ${result.taskSuggestionCount} task, and ${result.fileSuggestionCount} attachment suggestions. Suggestion-level keep or dismiss choices were reset for the rebuilt rows.`
      );
      setEmailReviewFeedbackTone('success');
      await refreshAdminData('email');
      await reloadSelectedEmailReview(selectedEmailReviewId);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  function handleOpenTaskCreateForm(suggestionId: number) {
    if (!selectedEmailReviewDetail) return;
    setOpenTaskCreateSuggestionId(suggestionId);
    setEmailReviewFeedback(null);
    setEmailTaskCreateDrafts((current) => {
      if (current[suggestionId]) return current;
      const draft = buildEmailTaskCreateDraft(selectedEmailReviewDetail, suggestionId);
      if (!draft) return current;
      return {
        ...current,
        [suggestionId]: draft,
      };
    });
  }

  function handleTaskCreateDraftChange(
    suggestionId: number,
    field: keyof EmailTaskCreateDraft,
    value: string
  ) {
    setEmailTaskCreateDrafts((current) => ({
      ...current,
      [suggestionId]: {
        ...(current[suggestionId] || { title: '', description: '', dueDate: '', priority: '' }),
        [field]: value,
      },
    }));
  }

  async function handleCreateTaskFromSuggestion(suggestionId: number) {
    if (!selectedEmailReviewId) return;
    const draft = emailTaskCreateDrafts[suggestionId];
    if (!draft) return;

    setEmailReviewLoading(true);
    setEmailReviewFeedback(null);
    try {
      const result = await companyAiService.createTaskFromEmailSuggestion({
        suggestionId,
        title: draft.title,
        description: draft.description,
        dueDate: draft.dueDate || null,
        priority: (draft.priority || null) as 'low' | 'medium' | 'high' | null,
      });
      setEmailReviewFeedback(
        result.alreadyCreated
          ? `Task ${result.createdTaskId} was already linked to suggestion ${suggestionId}.`
          : `Created PM task ${result.createdTaskId} from email suggestion ${suggestionId}.`
      );
      setEmailReviewFeedbackTone('success');
      await refreshAdminData('email');
      await reloadSelectedEmailReview(selectedEmailReviewId);
      setOpenTaskCreateSuggestionId(null);
    } catch (error) {
      setEmailReviewFeedback(normalizeErrorMessage(error));
      setEmailReviewFeedbackTone('error');
    } finally {
      setEmailReviewLoading(false);
    }
  }

  function getWorkflowHandoffAvailability(handoff: WorkflowHandoffDraft) {
    if (!selectedEmailReviewDetail) {
      return { canActivate: false, reason: 'Select an email first.' };
    }

    if (handoff.handoffType === 'reminder_draft') {
      return { canActivate: false, reason: 'Reminder drafts stay review-only for now.' };
    }

    if (handoff.handoffType === 'task_draft') {
      const taskDraft = buildTaskCreateDraftFromWorkflowHandoff(handoff);
      if (!taskDraft) {
        return { canActivate: false, reason: 'The linked task suggestion payload is incomplete.' };
      }

      const suggestion = selectedEmailReviewDetail.taskSuggestions.find((item) => item.emailTaskSuggestionId === taskDraft.suggestionId);
      if (!suggestion) {
        return { canActivate: false, reason: 'The linked task suggestion is no longer available on this email.' };
      }
      if (suggestion.createdTaskId) {
        return { canActivate: false, reason: `Task ${suggestion.createdTaskId} was already created from this suggestion.` };
      }
      if (suggestion.reviewStatus !== 'accepted') {
        return { canActivate: false, reason: 'Accept the linked task suggestion first, then open the create-task form.' };
      }
      if (!suggestion.canCreateTask) {
        return { canActivate: false, reason: suggestion.taskCreationBlockedReason || 'This linked task suggestion is not eligible for PM task creation.' };
      }
    }

    return { canActivate: true, reason: null as string | null };
  }

  function handleActivateWorkflowHandoff(handoff: WorkflowHandoffDraft) {
    if (!selectedEmailReviewDetail) return;
    const availability = getWorkflowHandoffAvailability(handoff);
    if (!availability.canActivate) {
      setEmailReviewFeedback(availability.reason);
      setEmailReviewFeedbackTone('error');
      return;
    }

    const actionLabel = getWorkflowHandoffActionLabel(handoff) || 'Continue';
    const confirmed = window.confirm(`${actionLabel}?\n\n${handoff.title}\n\n${handoff.reason}`);
    if (!confirmed) {
      return;
    }

    if (handoff.handoffType === 'task_draft') {
      const taskDraft = buildTaskCreateDraftFromWorkflowHandoff(handoff);
      if (!taskDraft) {
        setEmailReviewFeedback('The linked task handoff is missing its task suggestion payload.');
        setEmailReviewFeedbackTone('error');
        return;
      }
      handleOpenTaskCreateForm(taskDraft.suggestionId);
      setEmailTaskCreateDrafts((current) => ({
        ...current,
        [taskDraft.suggestionId]: taskDraft.draft,
      }));
      setEmailReviewFeedback(`Loaded the workflow handoff into task suggestion ${taskDraft.suggestionId}. Review the draft and confirm task creation when ready.`);
      setEmailReviewFeedbackTone('success');
      return;
    }

    if (handoff.handoffType === 'quote_follow_up_draft') {
      navigate('/quotations/workflow', {
        state: buildQuotationWorkflowStateFromWorkflowHandoff(handoff, selectedEmailReviewDetail),
      });
      return;
    }

    if (handoff.handoffType === 'invoice_follow_up_draft') {
      navigate('/invoices/workflow', {
        state: buildInvoiceWorkflowStateFromWorkflowHandoff(handoff, selectedEmailReviewDetail),
      });
    }
  }

  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);
  const visibleProjectSuggestions = getVisibleProjectSuggestions(selectedEmailReviewDetail);
  const signedInEmail = String(user?.userDetails || '').trim().toLowerCase();
  const enabledQuotas = quotas.filter((quota) => quota.isEnabled);
  const readySources = sources.filter((source) => source.status === 'ready' && source.isEnabled);
  const processingSources = sources.filter((source) => source.status === 'processing' || source.status === 'queued');
  const erroredSources = sources.filter((source) => source.status === 'error');
  const companyScopedSources = sources.filter((source) => source.scopeType === 'company');
  const selectedLocalUploadEntries = localUploadEntries.filter((entry) => entry.selected);
  const selectedLocalUploadCount = selectedLocalUploadEntries.length;
  const selectedLocalUploadBytes = selectedLocalUploadEntries.reduce((sum, entry) => sum + entry.sizeBytes, 0);
  const selectedSharePointEntries = sharePointFolderFiles.filter((file) => selectedSharePointFilePaths.includes(file.sourceFilePath));
  const selectedSharePointCount = selectedSharePointEntries.length;
  const structuredPdfSourceFiles = ingestingKnowledgeSourceId
    ? (selectedSourceFilesBySourceId[Number(ingestingKnowledgeSourceId)] || []).filter((file) => file.isPdf)
    : [];
  const knowledgeSourceCategoryOptions = [
    ...KNOWLEDGE_SOURCE_CATEGORY_OPTIONS.map((category) => ({
      value: category,
      label: category,
    })),
    ...(title && !KNOWLEDGE_SOURCE_CATEGORY_OPTIONS.includes(title as (typeof KNOWLEDGE_SOURCE_CATEGORY_OPTIONS)[number])
      ? [{ value: title, label: `${title} (existing)` }]
      : []),
  ];
  const quotaByEmail = new Map(
    quotas.map((quota) => [quota.ownerEmail.trim().toLowerCase(), quota] as const)
  );
  const quotaUserOptions = [
    ...quotaUsers.map((userOption) => {
      const quota = quotaByEmail.get(userOption.email);
      return {
        value: userOption.email,
        label: quota
          ? `${userOption.displayName} · ${formatTokenCount(quota.monthlyTokenLimit)} limit · ${quota.isEnabled ? 'Enabled' : 'Disabled'}`
          : `${userOption.displayName} · ${userOption.email} · no quota assigned yet`,
      };
    }),
    ...(!signedInEmail || quotaUsers.some((userOption) => userOption.email === signedInEmail)
      ? []
      : [{
          value: signedInEmail,
          label: `${signedInEmail} · no quota assigned yet`,
        }]),
  ];
  const quotaEmailSelectValue = quotaEmailMode === 'custom'
    ? CUSTOM_QUOTA_EMAIL_OPTION
    : quotaEmail.trim().toLowerCase();
  const availableQuotaPreview = quotaUsers.slice(0, 6);
  const currentModelOption = modelSettings?.options.find((option) => option.value === modelSettings.pmQueryModelSize) || null;
  const selectedModelOption = modelSettings?.options.find((option) => option.value === pmQueryModelSize) || null;
  const currentParserOption = modelSettings?.intentProviderOptions?.find((option) => option.value === modelSettings.pmQueryIntentProvider) || null;
  const selectedParserOption = modelSettings?.intentProviderOptions?.find((option) => option.value === pmQueryIntentProvider) || null;
  const filteredKnowledgeDocuments = knowledgeDocuments.filter((document) => {
    const filter = knowledgeDocumentFilter.trim().toLowerCase();
    if (!filter) return true;
    return [
      document.title,
      document.fileName,
      document.documentType,
      document.businessDomain,
      document.sourceFilePath,
    ].join(' ').toLowerCase().includes(filter);
  });

  return (
    <div className="company-ai-shell">
      <PageHeader
        title="Company AI Admin"
        subtitle="Manage per-user token quotas and register the reference sources that Company AI can cite."
        itemCount={quotas.length + sources.length}
        gradientColor="linear-gradient(135deg, #22314d 0%, #2251b4 50%, #5c6f8e 100%)"
        icon="🛠️"
        searchEnabled={false}
        sortEnabled={false}
        userLabel={user?.userDetails || 'Unknown'}
        roleLabel={roleLabel}
      />

      <CompanyAiNavigationTabs
        currentPage="admin"
        activeWorkspaceTab="ask-ai"
        userEmail={user?.userDetails}
        userRoles={user?.userRoles}
      />

      <PageSection width="wide" padding="compact" className="company-ai-admin-section company-ai-admin-section--hero">
        <div className="company-ai-admin-hero">
          <Card className="company-ai-admin-hero-card" padding="md">
            <div className="company-ai-admin-hero-content">
              <div className="company-ai-admin-eyebrow">Admin controls</div>
              <h2 className="company-ai-admin-hero-lead">Keep the AI workspace controlled, auditable, and easy to tune.</h2>
              <p className="company-ai-admin-hero-copy">
                Admin tools here manage who can spend tokens and which SharePoint or uploaded sources become part of the indexed company reference layer.
              </p>
              <div className="company-ai-chip-row">
                <span className="company-ai-chip">{quotas.length} quota profile{quotas.length === 1 ? '' : 's'}</span>
                <span className="company-ai-chip">{sources.length} registered source{sources.length === 1 ? '' : 's'}</span>
                <span className="company-ai-chip">{knowledgeDocuments.length} structured doc{knowledgeDocuments.length === 1 ? '' : 's'}</span>
              </div>
            </div>
          </Card>

          <Card className="company-ai-admin-summary-card" padding="md">
            <CardHeader
              title="Current state"
              subtitle="A quick pulse check before you change quotas or knowledge inputs."
              actions={<StatusBadge status={erroredSources.length ? 'warning' : 'active'}>{erroredSources.length ? 'Needs review' : 'Healthy'}</StatusBadge>}
            />
            <div className="company-ai-admin-kpi-grid">
              <div className="company-ai-kpi-card">
                <div className="company-ai-kpi-value">{enabledQuotas.length}</div>
                <div className="company-ai-kpi-label">Enabled quota accounts</div>
              </div>
              <div className="company-ai-kpi-card">
                <div className="company-ai-kpi-value">{readySources.length}</div>
                <div className="company-ai-kpi-label">Ready knowledge sources</div>
              </div>
              <div className="company-ai-kpi-card">
                <div className="company-ai-kpi-value">{processingSources.length + erroredSources.length}</div>
                <div className="company-ai-kpi-label">Sources needing attention</div>
              </div>
            </div>
          </Card>
        </div>
      </PageSection>

      <PageSection width="wide" padding="compact" className="company-ai-admin-section company-ai-admin-section--main">
        <div className="company-ai-admin-tab-bar" role="tablist" aria-label="AI admin sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeAdminTab === 'quota'}
            className={`company-ai-admin-tab${activeAdminTab === 'quota' ? ' company-ai-admin-tab--active' : ''}`}
            onClick={() => setActiveAdminTab('quota')}
          >
            User Quota
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeAdminTab === 'knowledge'}
            className={`company-ai-admin-tab${activeAdminTab === 'knowledge' ? ' company-ai-admin-tab--active' : ''}`}
            onClick={() => setActiveAdminTab('knowledge')}
          >
            Knowledge Sources
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeAdminTab === 'model'}
            className={`company-ai-admin-tab${activeAdminTab === 'model' ? ' company-ai-admin-tab--active' : ''}`}
            onClick={() => setActiveAdminTab('model')}
          >
            PM Query Model
          </button>
        </div>

        <div className="company-ai-layout company-ai-layout--admin">
          <div className="company-ai-primary">
            {activeAdminTab === 'quota' ? (
            <FormSection
              title="User Quotas"
              description="Monthly token limits are enforced per signed-in staff user."
              icon="🎚️"
              accentColor="#2251b4"
              actions={<StatusBadge status={enabledQuotas.length ? 'active' : 'inactive'}>{enabledQuotas.length} enabled</StatusBadge>}
              density="compact"
            >
              <form onSubmit={handleSaveQuota} className="company-ai-stacked">
                <div className={quotaEmailMode === 'custom' ? 'company-ai-form-grid company-ai-form-grid--quota-user' : 'company-ai-stacked'}>
                  <Select
                    label="User email"
                    value={quotaEmailSelectValue}
                    onChange={(event) => handleQuotaSelectionChange(event.target.value)}
                    options={[
                      ...quotaUserOptions,
                      { value: CUSTOM_QUOTA_EMAIL_OPTION, label: 'Custom email…' },
                    ]}
                    placeholder={quotaUsersBusy ? 'Loading available users...' : 'Select an assigned quota'}
                    density="compact"
                    fullWidth
                  />

                  {quotaEmailMode === 'custom' ? (
                    <Input
                      label="Custom email"
                      type="email"
                      value={quotaEmail}
                      onChange={(event) => setQuotaEmail(event.target.value)}
                      placeholder="user@linfinityeng.com"
                      density="compact"
                      fullWidth
                    />
                  ) : null}
                </div>

                <div className="company-ai-muted">
                  {quotaUsersBusy
                    ? 'Loading active users from Users/UserList so you can pre-seed a quota.'
                    : quotaUsersMessage
                      ? quotaUsersMessage
                      : 'Select an assigned quota to edit it, or choose Custom email to pre-seed a new user before they start using AI.'}
                </div>

                <div className="company-ai-form-grid company-ai-form-grid--quota-compact">
                  <Input
                    label="Monthly token limit"
                    type="number"
                    min="1"
                    value={quotaLimit}
                    onChange={(event) => setQuotaLimit(event.target.value)}
                    density="compact"
                    fullWidth
                  />
                  <Select
                    label="Quota status"
                    value={quotaEnabled ? 'enabled' : 'disabled'}
                    onChange={(event) => setQuotaEnabled(event.target.value === 'enabled')}
                    options={[
                      { value: 'enabled', label: 'Enabled' },
                      { value: 'disabled', label: 'Disabled' },
                    ]}
                    density="compact"
                    fullWidth
                  />
                  <Textarea
                    label="Notes"
                    value={quotaNotes}
                    onChange={(event) => setQuotaNotes(event.target.value)}
                    rows={2}
                    density="compact"
                    fullWidth
                  />
                </div>

                <div className="company-ai-form-actions">
                  <Button type="submit" loading={savingQuota}>Save Quota</Button>
                </div>

                {quotaFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${quotaFeedbackTone}`}>
                    {quotaFeedback}
                  </div>
                ) : null}
              </form>
            </FormSection>
            ) : null}

            {activeAdminTab === 'model' ? (
            <FormSection
              title="PM Query Model"
              description="Choose the OpenAI model size PM Query should request when model-backed API calls are enabled."
              icon="⚙️"
              accentColor="#2251b4"
              actions={<StatusBadge status={modelSettings ? 'active' : 'warning'}>{currentParserOption?.label || currentModelOption?.label || 'Default'}</StatusBadge>}
              density="compact"
            >
              <form onSubmit={handleSaveModelSettings} className="company-ai-stacked">
                <Select
                  label="Model size"
                  value={pmQueryModelSize}
                  onChange={(event) => setPmQueryModelSize(event.target.value as CompanyAiPmQueryModelSize)}
                  options={(modelSettings?.options || [
                    { value: 'mini' as const, label: 'GPT mini', model: 'gpt-5-mini', description: '' },
                    { value: 'gpt5' as const, label: 'GPT-5.x', model: 'gpt-5.4', description: '' },
                  ]).map((option) => ({
                    value: option.value,
                    label: `${option.label} · ${option.model}`,
                  }))}
                  density="compact"
                  fullWidth
                />

                <Select
                  label="Intent parser"
                  value={pmQueryIntentProvider}
                  onChange={(event) => setPmQueryIntentProvider(event.target.value as CompanyAiPmQueryIntentProvider)}
                  options={(modelSettings?.intentProviderOptions || [
                    { value: 'deterministic' as const, label: 'Deterministic', model: null, description: '' },
                    { value: 'openai' as const, label: 'OpenAI parser', model: 'gpt-5-mini', description: '' },
                    { value: 'deepseek' as const, label: 'DeepSeek parser', model: 'deepseek-flash', description: '' },
                  ]).map((option) => ({
                    value: option.value,
                    label: option.model ? `${option.label} · ${option.model}` : option.label,
                  }))}
                  density="compact"
                  fullWidth
                />

                <div className="company-ai-result-box company-ai-result-box--compact">
                  <div className="company-ai-section-title company-ai-section-title--flush">Selected API model</div>
                  <div className="company-ai-message-body">
                    {selectedModelOption ? `${selectedModelOption.label}: ${selectedModelOption.model}` : 'GPT mini'}
                  </div>
                  <div className="company-ai-muted" style={{ marginTop: '8px' }}>
                    {selectedModelOption?.description || 'The API key stays the same; the backend changes only the requested model id.'}
                  </div>
                  <div className="company-ai-section-title" style={{ marginTop: '12px' }}>Selected parser</div>
                  <div className="company-ai-message-body">
                    {selectedParserOption ? `${selectedParserOption.label}${selectedParserOption.model ? `: ${selectedParserOption.model}` : ''}` : 'Deterministic'}
                  </div>
                  <div className="company-ai-muted" style={{ marginTop: '8px' }}>
                    {selectedParserOption?.description || 'Parser hints are schema-checked before the PM Query plan is built.'}
                  </div>
                </div>

                <div className="company-ai-muted">
                  Model-backed parsers only provide structured hints. PM Query still validates the target, filters, relations, and read-only retrieval plan before running backend tools.
                </div>

                <div className="company-ai-form-actions">
                  <Button type="submit" loading={savingModelSettings}>Save Model</Button>
                </div>

                {modelFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${modelFeedbackTone}`}>
                    {modelFeedback}
                  </div>
                ) : null}
              </form>
            </FormSection>
            ) : null}

            {activeAdminTab === 'knowledge' ? (
            <FormSection
              title="Knowledge Sources"
              description="Drop local reference files, add a category and short description, then store them in KnowledgeBase before you build the AI index."
              icon="📚"
              accentColor="#2251b4"
              actions={<StatusBadge status={readySources.length ? 'active' : 'inactive'}>{readySources.length} ready</StatusBadge>}
              density="compact"
            >
              <form onSubmit={handleEnqueueSource} className="company-ai-stacked">
                <div className="company-ai-result-box company-ai-result-box--compact company-ai-upload-dropzone">
                  <div className="company-ai-section-title company-ai-section-title--flush">Reference file drop</div>
                  <div className="company-ai-muted">
                    Upload the files you want PMapp to store in `KnowledgeBase`. Saving this form creates the blob + SQL record only.
                  </div>
                  <input
                    className="company-ai-native-file-input"
                    type="file"
                    multiple
                    onChange={(event) => handleLocalUploadSelection(event, 'uploaded_folder')}
                  />
                  <div className="company-ai-upload-hint">
                    After the source is stored, use <strong>Build Index</strong> from the saved source card to make it searchable in AI.
                  </div>
                </div>

                <Select
                  label="Category"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  options={knowledgeSourceCategoryOptions}
                  placeholder="Select a category"
                  helpText="Choose from the preset categories. PMapp still stores this in the current source title field for SQL compatibility, so no database migration is needed for this simplified flow."
                  fullWidth
                />

                <Textarea
                  label="Short description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  rows={3}
                  helpText="Keep it short and practical so admins know what this reference set is for later."
                  fullWidth
                />

                {localUploadEntries.length ? (
                  <div className="company-ai-result-box company-ai-result-box--compact">
                    <div className="company-ai-section-title company-ai-section-title--flush">Selected reference files</div>
                    <div className="company-ai-muted">
                      Uncheck anything you do not want copied into `KnowledgeBase`.
                    </div>
                    <div className="company-ai-file-checklist">
                      {localUploadEntries.map((entry) => (
                        <label key={entry.localId} className="company-ai-file-row">
                          <input
                            type="checkbox"
                            checked={entry.selected}
                            onChange={() => toggleLocalUploadSelection(entry.localId)}
                          />
                          <div className="company-ai-file-copy">
                            <div className="company-ai-list-title">{entry.relativePath}</div>
                            <div className="company-ai-list-subtitle">
                              {formatFileSize(entry.sizeBytes)}
                              {entry.isPdf ? ' · PDF' : ''}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="company-ai-muted">No reference files have been picked yet.</div>
                )}

                <div className="company-ai-form-actions">
                  <Button type="submit" loading={savingSource}>
                    {editingSourceId ? 'Save Details' : 'Store Files'}
                  </Button>
                  {editingSourceId ? (
                    <Button type="button" variant="secondary" onClick={resetKnowledgeSourceDraft}>
                      Cancel Edit
                    </Button>
                  ) : null}
                  <span className="company-ai-muted">
                    {selectedLocalUploadCount
                      ? `${selectedLocalUploadCount} file${selectedLocalUploadCount === 1 ? '' : 's'} selected · ${formatFileSize(selectedLocalUploadBytes)}`
                      : editingSourceId
                        ? 'Leave the upload box empty to keep the currently stored files.'
                        : 'Drop or choose one or more reference files.'}
                  </span>
                </div>

                {sourceFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${sourceFeedbackTone}`}>
                    {sourceFeedback}
                  </div>
                ) : null}
              </form>
            </FormSection>
            ) : null}

            {activeAdminTab === 'knowledge' ? (
            <FormSection
              title="Page Index (Optional)"
              description="After a source is stored, choose its PDFs here if you also want structured page and section records."
              icon="🧩"
              accentColor="#2251b4"
              actions={<StatusBadge status={companyScopedSources.length ? 'active' : 'inactive'}>{companyScopedSources.length} company sources</StatusBadge>}
              density="compact"
            >
              <form onSubmit={handleIngestKnowledge} className="company-ai-stacked">
                <Select
                  label="Company knowledge source"
                  value={ingestingKnowledgeSourceId}
                  onChange={(event) => setIngestingKnowledgeSourceId(event.target.value)}
                  options={[
                    { value: '', label: 'Select a source' },
                    ...companyScopedSources.map((source) => ({
                      value: String(source.aiKnowledgeSourceId),
                      label: buildKnowledgeSourceSelectLabel(
                        source,
                        selectedSourceFilesBySourceId[source.aiKnowledgeSourceId],
                      ),
                    })),
                  ]}
                  fullWidth
                />
                <div className="company-ai-result-box company-ai-result-box--compact">
                  <div className="company-ai-section-title company-ai-section-title--flush">Stored PDF picker</div>
                  <div className="company-ai-muted">
                    This optional step runs only against the checked PDFs already stored for this source in `KnowledgeBase`.
                  </div>
                  {structuredPdfSourceFiles.length ? (
                    <div className="company-ai-file-checklist">
                      {structuredPdfSourceFiles.map((file) => (
                        <label key={file.aiKnowledgeSourceFileId} className="company-ai-file-row">
                          <input
                            type="checkbox"
                            checked={selectedPdfFileIds.includes(file.aiKnowledgeSourceFileId)}
                            onChange={() => setSelectedPdfFileIds((current) => (
                              current.includes(file.aiKnowledgeSourceFileId)
                                ? current.filter((value) => value !== file.aiKnowledgeSourceFileId)
                                : [...current, file.aiKnowledgeSourceFileId]
                            ))}
                          />
                          <div className="company-ai-file-copy">
                            <div className="company-ai-list-title">{file.relativePath || file.fileName}</div>
                            <div className="company-ai-list-subtitle">
                              {formatFileSize(file.sizeBytes)}
                              {file.sourceUrl ? ' · SharePoint-backed' : ' · Local upload'}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="company-ai-muted">Choose a source that already has stored PDF files.</div>
                  )}
                </div>
                <div className="company-ai-form-actions">
                  <Button type="submit" loading={runningKnowledgeIngest}>Build Page Index</Button>
                  <span className="company-ai-muted">
                    Use this when you want page/section structure in addition to the normal AI search index.
                  </span>
                </div>
                {knowledgeFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${knowledgeFeedbackTone}`}>
                    {knowledgeFeedback}
                  </div>
                ) : null}
              </form>
            </FormSection>
            ) : null}

            {activeAdminTab === 'email' ? (
            <Card
              header={(
                <CardHeader
                  title="Email Suggestion Review"
                  subtitle="Review suggestion-only email classifications, project matches, task suggestions, and attachment routing hints."
                  actions={<StatusBadge status={emailReviewList.length ? 'active' : 'warning'}>{emailReviewTotalCount} emails</StatusBadge>}
                />
              )}
            >
              <div className="company-ai-stacked">
                <div className="company-ai-form-grid company-ai-form-grid--quota-compact">
                  <Select
                    label="Classification"
                    value={emailReviewClassification}
                    onChange={(event) => setEmailReviewClassification(event.target.value)}
                    options={[
                      { value: '', label: 'All classes' },
                      { value: 'quote_inquiry', label: 'Quote inquiry' },
                      { value: 'project_update', label: 'Project update' },
                      { value: 'invoice_payment', label: 'Invoice/payment' },
                      { value: 'rfi_question', label: 'RFI/question' },
                      { value: 'document_submission', label: 'Document submission' },
                      { value: 'site_issue', label: 'Site issue' },
                      { value: 'general_admin', label: 'General admin' },
                      { value: 'low_value_noise', label: 'Low-value/noise' },
                    ]}
                    fullWidth
                  />
                  <Select
                    label="Queue"
                    value={emailReviewQueueMode}
                    onChange={(event) => setEmailReviewQueueMode((event.target.value || 'exceptions') as 'exceptions' | 'all')}
                    options={[
                      { value: 'exceptions', label: 'Exceptions only' },
                      { value: 'all', label: 'All inbound mail' },
                    ]}
                    fullWidth
                  />
                  <Select
                    label="Review status"
                    value={emailReviewStatusFilter}
                    onChange={(event) => setEmailReviewStatusFilter(event.target.value)}
                    options={[
                      { value: '', label: 'All review states' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'accepted', label: 'Accepted' },
                      { value: 'rejected', label: 'Rejected' },
                    ]}
                    fullWidth
                  />
                </div>

                <div className="company-ai-muted">All dates and times on this page are shown in Melbourne time.</div>

                {emailReviewFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${emailReviewFeedbackTone}`}>
                    {emailReviewFeedback}
                  </div>
                ) : null}

                <div className="company-ai-layout company-ai-layout--admin">
                  <div className="company-ai-primary">
                    {emailReviewList.length ? (
                      <div className="company-ai-list company-ai-list--inventory">
                        {emailReviewList.map((item) => (
                          <button
                            key={item.emailMessageId}
                            type="button"
                            className={`company-ai-list-button${selectedEmailReviewId === item.emailMessageId ? ' company-ai-list-button--active' : ''}`}
                            onClick={() => void handleSelectEmailReview(item.emailMessageId)}
                          >
                            <div className="company-ai-list-title">{item.subject || '(no subject)'}</div>
                            <div className="company-ai-list-subtitle">
                              {item.fromEmail || 'unknown sender'}
                              {' · '}
                              <EmailClassificationBadge value={item.classification} />
                              {' '}
                              {item.behaviorSignal ? `${formatBehaviorDisposition(item.behaviorSignal.disposition)} · ` : ''}
                              {formatDateTime(item.receivedAt)}
                            </div>
                            <div className="company-ai-muted company-ai-muted--spaced">
                              {item.bodyPreview || 'No preview available'}
                            </div>
                            {item.behaviorSignal ? (
                              <div className="company-ai-citation-copy">
                                {Math.round(item.behaviorSignal.confidence * 100)}% · {item.behaviorSignal.reviewPriority} priority
                              </div>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <EmptyState
                        icon="📬"
                        title="No email suggestions yet"
                        description="Ingest emails through the webhook or mailbox bridge and the review queue will appear here."
                        className="company-ai-admin-empty-state"
                      />
                    )}
                  </div>

                  <div className="company-ai-sidebar">
                    {selectedEmailReviewDetail ? (
                      <div className="company-ai-stacked">
                        <div className="company-ai-result-box">
                          <div className="company-ai-section-title company-ai-section-title--flush">{selectedEmailReviewDetail.message.subject}</div>
                          <div className="company-ai-email-toolbar">
                            <div className="company-ai-email-meta-grid">
                              <div className="company-ai-email-meta">
                                <span className="company-ai-email-meta-label">From</span>
                                <span className="company-ai-email-meta-value">{selectedEmailReviewDetail.message.fromEmail || 'unknown'}</span>
                              </div>
                              <div className="company-ai-email-meta">
                                <span className="company-ai-email-meta-label">Received</span>
                                <span className="company-ai-email-meta-value">{formatDateTime(selectedEmailReviewDetail.message.receivedAt)}</span>
                              </div>
                              <div className="company-ai-email-meta">
                                <span className="company-ai-email-meta-label">Classification</span>
                                <span className="company-ai-email-meta-value">
                                  <EmailClassificationBadge value={selectedEmailReviewDetail.message.classification} />
                                </span>
                              </div>
                              <div className="company-ai-email-meta">
                                <span className="company-ai-email-meta-label">Review status</span>
                                <span className="company-ai-email-meta-value">
                                  <EmailReviewStatusBadge value={selectedEmailReviewDetail.message.reviewStatus} />
                                </span>
                              </div>
                            </div>
                            <div className="company-ai-form-actions company-ai-form-actions--email-review">
                              <Button loading={emailReviewLoading} onClick={() => void handleReviewEmailMessage('accepted')}>
                                Keep For PM Follow-up
                              </Button>
                              <Button variant="secondary" loading={emailReviewLoading} onClick={() => void handleReviewEmailMessage('rejected')}>
                                Ignore For PM Follow-up
                              </Button>
                              <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReprocessEmailMessage()}>
                                Reprocess Suggestions
                              </Button>
                              <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleGenerateDraftAssistance()}>
                                Generate Draft Assist
                              </Button>
                            </div>
                          </div>
                          <div className="company-ai-message-caption">
                            {formatEmailClassification(selectedEmailReviewDetail.message.classification)} emails are classified from subject and body. Keep For PM Follow-up confirms this email is worth acting on inside PMapp.
                          </div>
                          <div className="company-ai-message-caption">
                            Direction: {selectedEmailReviewDetail.message.messageDirection}
                            {selectedEmailReviewDetail.message.folderName ? ` · ${selectedEmailReviewDetail.message.folderName}` : ''}
                            {selectedEmailReviewDetail.message.receivedAt ? ` · Melbourne ${formatDateTime(selectedEmailReviewDetail.message.receivedAt)}` : ''}
                            {selectedEmailReviewDetail.message.behaviorSignal
                              ? ` · ${formatBehaviorDisposition(selectedEmailReviewDetail.message.behaviorSignal.disposition)} ${Math.round(selectedEmailReviewDetail.message.behaviorSignal.confidence * 100)}%`
                              : ''}
                            {selectedEmailReviewDetail.message.inReplyToInternetMessageId ? ` · In-Reply-To recorded` : ''}
                            {selectedEmailReviewDetail.message.references.length ? ` · ${selectedEmailReviewDetail.message.references.length} references` : ''}
                          </div>
                          <div className="company-ai-message-body company-ai-message-body--email-preview">
                            {selectedEmailReviewDetail.message.bodyPreview || selectedEmailReviewDetail.message.normalizedBody || 'No body preview available'}
                          </div>
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Behavior learning</div>
                          {selectedEmailReviewDetail.message.behaviorSignal ? (
                            <div className="company-ai-stacked">
                              <div className="company-ai-citation">
                                <div className="company-ai-citation-title">
                                  {formatBehaviorDisposition(selectedEmailReviewDetail.message.behaviorSignal.disposition)}
                                  {' · '}
                                  {Math.round(selectedEmailReviewDetail.message.behaviorSignal.confidence * 100)}%
                                </div>
                                <div className="company-ai-citation-copy">
                                  {selectedEmailReviewDetail.message.behaviorSignal.reviewPriority} priority
                                  {' · '}
                                  {selectedEmailReviewDetail.message.behaviorSignal.requiresReview ? 'stays in review queue' : 'hidden from default exception queue'}
                                  {' · '}
                                  {selectedEmailReviewDetail.message.behaviorSignal.observedSampleSize} matched emails
                                  {typeof selectedEmailReviewDetail.message.behaviorSignal.avgReplyMinutes === 'number'
                                    ? ` · avg reply ${Math.round(selectedEmailReviewDetail.message.behaviorSignal.avgReplyMinutes)} min`
                                    : ''}
                                </div>
                              </div>
                              <div className="company-ai-citation-list">
                                {selectedEmailReviewDetail.message.behaviorSignal.reasons.map((reason) => (
                                  <div key={reason} className="company-ai-citation">
                                    <div className="company-ai-citation-copy">{reason}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="company-ai-muted">No stable reply/ignore/action pattern has been learned for this email yet.</div>
                          )}
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Project suggestions</div>
                          <div className="company-ai-message-caption">
                            Keep Match confirms the project link reviewers want this email to use. Dismissing a match teaches the ranking memory not to prefer it next time.
                          </div>
                          {visibleProjectSuggestions.length ? (
                            <div className="company-ai-citation-list">
                              {visibleProjectSuggestions.map((suggestion) => (
                                <div key={suggestion.emailProjectSuggestionId} className="company-ai-citation">
                                  <div className="company-ai-citation-title">
                                    {suggestion.title || `Project ${suggestion.projectId || 'unknown'}`}
                                    {' · '}
                                    {Math.round(suggestion.confidence * 100)}%
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    {formatReasonCodeList(suggestion.reasonCodes || []) || 'No ranking reasons recorded'}
                                  </div>
                                  {suggestion.evidence.length ? (
                                    <div className="company-ai-citation-copy">
                                      Evidence: {formatEvidenceSummary(suggestion.evidence)}
                                    </div>
                                  ) : null}
                                  <div className="company-ai-form-actions">
                                    <Button variant="secondary" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('project', suggestion.emailProjectSuggestionId, 'accepted')}>Keep Match</Button>
                                    <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('project', suggestion.emailProjectSuggestionId, 'rejected')}>Dismiss</Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="company-ai-muted">No project suggestions were generated.</div>
                          )}
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Task suggestions</div>
                          <div className="company-ai-message-caption">
                            Task suggestions come from explicit request phrases in the email. Keeping a suggestion does not create a PM task; it only keeps the draft suggestion eligible for the later Create Task step.
                          </div>
                          {selectedEmailReviewDetail.taskSuggestions.length ? (
                            <div className="company-ai-citation-list">
                              {selectedEmailReviewDetail.taskSuggestions.map((suggestion) => (
                                <div key={suggestion.emailTaskSuggestionId} className="company-ai-citation company-ai-citation--task">
                                  <div className="company-ai-citation-title company-ai-citation-title--with-status">
                                    <span>{suggestion.suggestedTitle}</span>
                                    <EmailReviewStatusBadge value={suggestion.createdTaskId ? 'accepted' : suggestion.reviewStatus} />
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    {formatReasonCodeList(suggestion.reasonCodes || []) || 'No extraction reasons recorded'}
                                    {suggestion.suggestedDueDate ? ` · due ${suggestion.suggestedDueDate}` : ''}
                                  </div>
                                  {suggestion.evidence.length ? (
                                    <div className="company-ai-citation-copy">
                                      Triggered by: {formatEvidenceSummary(suggestion.evidence)}
                                    </div>
                                  ) : null}
                                  <div className="company-ai-citation-copy">
                                    {suggestion.createdTaskId
                                      ? `Created task ${suggestion.createdTaskId}${suggestion.createdTaskAt ? ` · ${formatDateTime(suggestion.createdTaskAt)}` : ''}`
                                      : suggestion.canCreateTask
                                        ? 'Approved action can create a PM task'
                                        : suggestion.taskCreationBlockedReason || 'Review-only suggestion'}
                                  </div>
                                  <div className="company-ai-form-actions company-ai-form-actions--approval">
                                    <Button loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('task', suggestion.emailTaskSuggestionId, 'accepted')}>Approve Action Draft</Button>
                                    <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('task', suggestion.emailTaskSuggestionId, 'rejected')}>Dismiss</Button>
                                    {suggestion.reviewStatus === 'accepted' && suggestion.canCreateTask && !suggestion.createdTaskId ? (
                                      <Button
                                        loading={emailReviewLoading}
                                        onClick={() => handleOpenTaskCreateForm(suggestion.emailTaskSuggestionId)}
                                      >
                                        Create Task
                                      </Button>
                                    ) : null}
                                  </div>
                                  {openTaskCreateSuggestionId === suggestion.emailTaskSuggestionId ? (
                                    <div className="company-ai-stacked">
                                      <Input
                                        label="Project"
                                        value={
                                          selectedEmailReviewDetail.projectSuggestions.find((projectSuggestion) => projectSuggestion.projectId === suggestion.suggestedProjectId)?.title
                                          || (suggestion.suggestedProjectId ? `Project ${suggestion.suggestedProjectId}` : 'No matched project')
                                        }
                                        readOnly
                                        fullWidth
                                      />
                                      <Input
                                        label="Task title"
                                        value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.title || ''}
                                        onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'title', event.target.value)}
                                        fullWidth
                                      />
                                      <Textarea
                                        label="Task description"
                                        value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.description || ''}
                                        onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'description', event.target.value)}
                                        rows={3}
                                        fullWidth
                                      />
                                      <div className="company-ai-form-grid company-ai-form-grid--quota-compact">
                                        <Input
                                          label="Due date"
                                          type="date"
                                          value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.dueDate || ''}
                                          onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'dueDate', event.target.value)}
                                          fullWidth
                                        />
                                        <Select
                                          label="Priority"
                                          value={emailTaskCreateDrafts[suggestion.emailTaskSuggestionId]?.priority || ''}
                                          onChange={(event) => handleTaskCreateDraftChange(suggestion.emailTaskSuggestionId, 'priority', event.target.value)}
                                          options={[
                                            { value: '', label: 'Use suggestion/default' },
                                            { value: 'low', label: 'Low' },
                                            { value: 'medium', label: 'Medium' },
                                            { value: 'high', label: 'High' },
                                          ]}
                                          fullWidth
                                        />
                                      </div>
                                      <div className="company-ai-form-actions">
                                        <Button loading={emailReviewLoading} onClick={() => void handleCreateTaskFromSuggestion(suggestion.emailTaskSuggestionId)}>
                                          Confirm Create Task
                                        </Button>
                                        <Button variant="ghost" loading={emailReviewLoading} onClick={() => setOpenTaskCreateSuggestionId(null)}>
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="company-ai-muted">No task suggestions were generated.</div>
                          )}
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Attachment suggestions</div>
                          <div className="company-ai-message-caption">
                            Attachment suggestions are filing hints only. Keeping one records the preferred folder/category for review, but it does not move, upload, or rename the file yet.
                          </div>
                          {selectedEmailReviewDetail.fileSuggestions.length ? (
                            <div className="company-ai-citation-list">
                              {selectedEmailReviewDetail.fileSuggestions.map((suggestion) => (
                                <div key={suggestion.emailFileSuggestionId} className="company-ai-citation">
                                  <div className="company-ai-citation-title">{suggestion.attachmentName}</div>
                                  <div className="company-ai-citation-copy">
                                    {suggestion.suggestedCategory || 'other'}
                                    {suggestion.suggestedFolder ? ` · ${suggestion.suggestedFolder}` : ''}
                                  </div>
                                  {suggestion.suggestedQuotationNumber ? (
                                    <div className="company-ai-citation-copy">Quotation {suggestion.suggestedQuotationNumber}</div>
                                  ) : null}
                                  {suggestion.suggestedFileName ? (
                                    <div className="company-ai-citation-copy"><strong>File as:</strong> {suggestion.suggestedFileName}</div>
                                  ) : null}
                                  <div className="company-ai-citation-copy">
                                    {formatReasonCodeList(suggestion.reasonCodes || []) || 'No filing reasons recorded'}
                                  </div>
                                  <div className="company-ai-form-actions">
                                    <Button variant="secondary" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('file', suggestion.emailFileSuggestionId, 'accepted')}>Keep Filing Hint</Button>
                                    <Button variant="ghost" loading={emailReviewLoading} onClick={() => void handleReviewSuggestion('file', suggestion.emailFileSuggestionId, 'rejected')}>Dismiss</Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="company-ai-muted">No attachment suggestions were generated.</div>
                          )}
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Thread memory</div>
                          {selectedEmailReviewDetail.threadMessages.length ? (
                            <div className="company-ai-citation-list">
                              {selectedEmailReviewDetail.threadMessages.map((message) => (
                                <div key={message.emailMessageId} className="company-ai-citation">
                                  <div className="company-ai-citation-title">
                                    {message.messageDirection === 'outbound' ? 'Reply' : 'Inbound'}
                                    {' · '}
                                    {message.subject || `Email ${message.emailMessageId}`}
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    {message.fromEmail || 'unknown sender'}
                                    {message.receivedAt ? ` · ${formatDateTime(message.receivedAt)}` : ''}
                                    {message.classification ? ` · ${message.classification}` : ''}
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    {message.outcomeTypes.length ? message.outcomeTypes.join(', ') : 'No outcomes recorded yet'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="company-ai-muted">No thread history has been captured for this conversation yet.</div>
                          )}
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Reply pairs and outcomes</div>
                          {selectedEmailReviewDetail.replyPairs.length || selectedEmailReviewDetail.outcomes.length ? (
                            <div className="company-ai-citation-list">
                              {selectedEmailReviewDetail.replyPairs.map((pair) => (
                                <div key={pair.emailReplyPairId} className="company-ai-citation">
                                  <div className="company-ai-citation-title">
                                    Reply paired via {pair.pairingMethod}
                                    {' · '}
                                    {Math.round(pair.pairingConfidence * 100)}%
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    Inbound: {pair.inboundMessage?.subject || `Email ${pair.inboundEmailMessageId}`}
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    Reply: {pair.replyMessage?.subject || `Email ${pair.replyEmailMessageId}`}
                                  </div>
                                </div>
                              ))}
                              {selectedEmailReviewDetail.outcomes.map((outcome) => (
                                <div key={outcome.emailMessageOutcomeId} className="company-ai-citation">
                                  <div className="company-ai-citation-title">
                                    {outcome.outcomeType}
                                    {' · '}
                                    {outcome.outcomeStatus}
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    {outcome.recordedAt ? formatDateTime(outcome.recordedAt) : 'Recorded'}
                                    {outcome.relatedEmailMessageId ? ` · linked email ${outcome.relatedEmailMessageId}` : ''}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="company-ai-muted">No reply pairing or outcomes have been recorded for this email yet.</div>
                          )}
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Reviewer feedback memory</div>
                          {selectedEmailReviewDetail.reviewerFeedback.length ? (
                            <div className="company-ai-citation-list">
                              {selectedEmailReviewDetail.reviewerFeedback.map((feedback) => (
                                <div key={feedback.emailReviewerFeedbackId} className="company-ai-citation">
                                  <div className="company-ai-citation-title">
                                    {feedback.suggestionKind}
                                    {' · '}
                                    {feedback.reviewStatus}
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    {feedback.projectId ? `project ${feedback.projectId}` : feedback.suggestedTitle || feedback.suggestedCategory || 'email-level review'}
                                    {feedback.reviewedAt ? ` · ${formatDateTime(feedback.reviewedAt)}` : ''}
                                  </div>
                                  <div className="company-ai-citation-copy">
                                    {feedback.reviewNotes || 'No reviewer note recorded.'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="company-ai-muted">No reviewer feedback memory has been recorded for this email yet.</div>
                          )}
                        </div>

                        <div className="company-ai-result-box company-ai-result-box--compact">
                          <div className="company-ai-section-title company-ai-section-title--flush">Draft assistance</div>
                          {emailDraftAssistance ? (
                            <div className="company-ai-stacked">
                              <div className="company-ai-message-body">{emailDraftAssistance.internalSummary}</div>
                              <Input label="Reply subject" value={emailDraftAssistance.replyDraft.subject} readOnly fullWidth />
                              <Textarea label="Reply draft" value={emailDraftAssistance.replyDraft.body} rows={10} readOnly fullWidth />
                              {emailDraftAssistance.suggestedNextAction ? (
                                <div className="company-ai-citation">
                                  <div className="company-ai-citation-title">{emailDraftAssistance.suggestedNextAction.title}</div>
                                  <div className="company-ai-citation-copy">
                                    {emailDraftAssistance.suggestedNextAction.description}
                                    {emailDraftAssistance.suggestedNextAction.projectId ? ` · project ${emailDraftAssistance.suggestedNextAction.projectId}` : ''}
                                  </div>
                                </div>
                              ) : null}
                              {emailDraftAssistance.workflowHandoffs.length ? (
                                <div className="company-ai-citation-list">
                                  {emailDraftAssistance.workflowHandoffs.map((handoff) => (
                                    (() => {
                                      const availability = getWorkflowHandoffAvailability(handoff);
                                      const actionLabel = getWorkflowHandoffActionLabel(handoff);
                                      return (
                                        <div key={`${handoff.handoffType}-${handoff.title}`} className="company-ai-citation">
                                          <div className="company-ai-citation-title">
                                            {handoff.handoffType.replace(/_/g, ' ')}
                                            {handoff.recommended ? ' · recommended' : ' · optional'}
                                          </div>
                                          <div className="company-ai-citation-copy">
                                            {handoff.title}
                                            {handoff.projectId ? ` · project ${handoff.projectId}` : ''}
                                            {` · ${handoff.targetArea}`}
                                          </div>
                                          <div className="company-ai-citation-copy">{handoff.description}</div>
                                          <div className="company-ai-citation-copy">{handoff.reason}</div>
                                          {actionLabel ? (
                                            <div className="company-ai-form-actions">
                                              <Button
                                                variant="secondary"
                                                onClick={() => handleActivateWorkflowHandoff(handoff)}
                                                disabled={!availability.canActivate}
                                              >
                                                {actionLabel}
                                              </Button>
                                            </div>
                                          ) : null}
                                          {!availability.canActivate && availability.reason ? (
                                            <div className="company-ai-citation-copy">{availability.reason}</div>
                                          ) : null}
                                          <pre className="company-ai-message-body">{JSON.stringify(handoff.payload, null, 2)}</pre>
                                        </div>
                                      );
                                    })()
                                  ))}
                                </div>
                              ) : null}
                              <div className="company-ai-citation-list">
                                {emailDraftAssistance.guidance.map((item) => (
                                  <div key={item} className="company-ai-citation">
                                    <div className="company-ai-citation-copy">{item}</div>
                                  </div>
                                ))}
                              </div>
                              <div className="company-ai-citation">
                                <div className="company-ai-citation-title">
                                  Draft supervision
                                  {' · '}
                                  {emailDraftAssistance.supervision.exampleCount} example{emailDraftAssistance.supervision.exampleCount === 1 ? '' : 's'}
                                </div>
                                <div className="company-ai-citation-copy">
                                  {emailDraftAssistance.supervision.learnedGreeting ? `Greeting: ${emailDraftAssistance.supervision.learnedGreeting}` : 'No learned greeting'}
                                  {' · '}
                                  {emailDraftAssistance.supervision.learnedSignOff ? `Sign-off: ${emailDraftAssistance.supervision.learnedSignOff}` : 'No learned sign-off'}
                                </div>
                              </div>
                              {emailDraftAssistance.supervision.toneNotes.length ? (
                                <div className="company-ai-citation-list">
                                  {emailDraftAssistance.supervision.toneNotes.map((note) => (
                                    <div key={note} className="company-ai-citation">
                                      <div className="company-ai-citation-copy">{note}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                              {emailDraftAssistance.supervision.examples.length ? (
                                <div className="company-ai-citation-list">
                                  {emailDraftAssistance.supervision.examples.map((example) => (
                                    <div key={`${example.inboundEmailMessageId}-${example.replyEmailMessageId}`} className="company-ai-citation">
                                      <div className="company-ai-citation-title">
                                        {example.matchScope.replace(/_/g, ' ')}
                                        {' · '}
                                        {example.replySubject || `Reply ${example.replyEmailMessageId}`}
                                      </div>
                                      <div className="company-ai-citation-copy">
                                        Inbound: {example.inboundSubject || `Email ${example.inboundEmailMessageId}`}
                                        {example.pairedAt ? ` · ${formatDateTime(example.pairedAt)}` : ''}
                                        {example.pairingMethod ? ` · ${example.pairingMethod}` : ''}
                                      </div>
                                      <div className="company-ai-citation-copy">{example.replyExcerpt || 'No reply excerpt available.'}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ) : (
                            <div className="company-ai-muted">Generate draft assistance to prepare a human-reviewed internal summary and reply draft.</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <EmptyState
                        icon="🔎"
                        title="Select an email"
                        description="Choose a recent ingested email to inspect the suggestion-only review detail."
                        className="company-ai-admin-empty-state"
                      />
                    )}
                  </div>
                </div>
              </div>
            </Card>
            ) : null}

            {activeAdminTab === 'model' ? (
            <Card
              header={(
                <CardHeader
                  title="Current PM Query Model"
                  subtitle="This setting is read by the PM Query API before each run."
                  actions={<StatusBadge status={currentModelOption ? 'active' : 'warning'}>{currentParserOption?.label || currentModelOption?.label || 'Default'}</StatusBadge>}
                />
              )}
            >
              <div className="company-ai-stacked">
                <div className="company-ai-result-box company-ai-result-box--compact">
                  <div className="company-ai-section-title company-ai-section-title--flush">Active setting</div>
                  <div className="company-ai-message-body">
                    {currentModelOption ? `${currentModelOption.label}\n${currentModelOption.model}` : 'GPT mini\ngpt-5-mini'}
                    {'\n'}
                    {currentParserOption ? `${currentParserOption.label}${currentParserOption.model ? `\n${currentParserOption.model}` : ''}` : 'Deterministic parser'}
                  </div>
                </div>
                <div className="company-ai-citation-list">
                  {(modelSettings?.options || []).map((option) => (
                    <div key={option.value} className="company-ai-citation">
                      <div className="company-ai-citation-title">{option.label}</div>
                      <div className="company-ai-citation-copy">
                        {option.model}
                        {' · '}
                        {option.description}
                      </div>
                    </div>
                  ))}
                  {(modelSettings?.intentProviderOptions || []).map((option) => (
                    <div key={`parser-${option.value}`} className="company-ai-citation">
                      <div className="company-ai-citation-title">{option.label}</div>
                      <div className="company-ai-citation-copy">
                        {option.model || 'No model call'}
                        {' · '}
                        {option.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
            ) : null}

            {activeAdminTab === 'knowledge' ? (
            <Card
              header={(
                <CardHeader
                  title="Document Structure Viewer"
                  subtitle="Inspect how one structured PDF was broken into sections, blocks, rules, workflows, and extracted terms."
                  actions={<StatusBadge status={selectedKnowledgeDocumentStructure ? 'active' : 'warning'}>{selectedKnowledgeDocumentStructure ? 'Loaded' : 'Select a document'}</StatusBadge>}
                />
              )}
            >
              {selectedKnowledgeDocumentStructure ? (
                <div className="company-ai-stacked">
                  <div className="company-ai-result-box">
                    <div className="company-ai-section-title company-ai-section-title--flush">Document</div>
                    <div className="company-ai-message-body">
                      {selectedKnowledgeDocumentStructure.document.title}
                      {'\n'}
                      {selectedKnowledgeDocumentStructure.document.documentType || 'unknown type'}
                      {' · '}
                      {selectedKnowledgeDocumentStructure.document.businessDomain || 'unknown domain'}
                    </div>
                  </div>

                  <div className="company-ai-result-grid">
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Sections</div>
                      <div className="company-ai-result-value">{selectedKnowledgeDocumentStructure.sections.length}</div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Blocks</div>
                      <div className="company-ai-result-value">{selectedKnowledgeDocumentStructure.blocks.length}</div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Rules</div>
                      <div className="company-ai-result-value">{selectedKnowledgeDocumentStructure.rules.length}</div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Workflows</div>
                      <div className="company-ai-result-value">{selectedKnowledgeDocumentStructure.workflows.length}</div>
                    </div>
                  </div>

                  {selectedKnowledgeDocumentStructure.sections.length ? (
                    <div className="company-ai-citation-list">
                      {selectedKnowledgeDocumentStructure.sections.slice(0, 12).map((section) => (
                        <div key={section.knowledgeSectionId} className="company-ai-citation">
                          <div className="company-ai-citation-title">{section.headingText}</div>
                          <div className="company-ai-citation-copy">
                            level {section.headingLevel}
                            {section.pageStart ? ` · page ${section.pageStart}` : ''}
                            {section.sectionSummary ? ` · ${section.sectionSummary}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <EmptyState
                  icon="🗂️"
                  title="No document selected"
                  description="Choose a structured knowledge document from the admin inventory to inspect its extracted sections and blocks."
                />
              )}
            </Card>
            ) : null}
          </div>

          <div className="company-ai-sidebar">
            {activeAdminTab === 'quota' ? (
            <Card
              header={(
                <CardHeader
                  title="Current Quotas"
                  subtitle="Live usage rolls up from the current month."
                  actions={<StatusBadge status={String(quotas.length)}>{quotas.length} users</StatusBadge>}
                />
              )}
            >
              {quotas.length ? (
                <div className="company-ai-list company-ai-list--static">
                  {quotas.map((quota) => (
                    <div key={quota.ownerEmail} className="company-ai-list-button company-ai-list-button--static company-ai-list-button--active">
                      <div className="company-ai-list-title">{quota.ownerEmail}</div>
                      <div className="company-ai-list-subtitle">
                        {quota.usedTokensThisPeriod.toLocaleString()} used
                        {' / '}
                        {quota.monthlyTokenLimit.toLocaleString()} limit
                        {' · '}
                        {quota.isEnabled ? 'Enabled' : 'Disabled'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="company-ai-stacked">
                  <EmptyState
                    icon="👥"
                    title="No quota records yet"
                    description="The first AI use creates a default quota automatically, or you can pre-seed one here."
                    className="company-ai-admin-empty-state"
                  />
                  {availableQuotaPreview.length ? (
                    <div className="company-ai-result-box company-ai-result-box--compact">
                      <div className="company-ai-section-title company-ai-section-title--flush">Available staff to pre-seed</div>
                      <div className="company-ai-source-strip">
                        {availableQuotaPreview.map((userOption) => (
                          <span key={userOption.email} className="company-ai-source-pill">
                            {userOption.displayName}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </Card>
            ) : null}

            {activeAdminTab === 'knowledge' ? (
            <Card
              header={(
                <CardHeader
                  title="Registered Sources"
                  subtitle="Stored references become searchable after you build the AI index for that source."
                  actions={<StatusBadge status={String(sources.length)}>{sources.length} sources</StatusBadge>}
                />
              )}
            >
              {sources.length ? (
                <div className="company-ai-list company-ai-list--inventory">
                  {sources.map((source) => (
                    <div key={source.aiKnowledgeSourceId} className="company-ai-list-button company-ai-list-button--static company-ai-list-button--active">
                      <div className="company-ai-list-title">{source.title}</div>
                      <div className="company-ai-list-subtitle">
                        {formatKnowledgeSourceKind(source.sourceType)}
                        {' · '}
                        {source.status}
                        {' · '}
                        {source.status === 'ready' ? `Indexed ${formatDateTime(source.lastIndexedAt)}` : `Stored ${formatDateTime(source.modifiedDate)}`}
                      </div>
                      {source.description ? (
                        <div className="company-ai-muted company-ai-muted--spaced">{source.description}</div>
                      ) : null}
                      <div className="company-ai-list-subtitle">
                        {source.selectedFileCount} file{source.selectedFileCount === 1 ? '' : 's'}
                        {' · '}
                        {source.pdfFileCount} PDF{source.pdfFileCount === 1 ? '' : 's'}
                        {' · '}
                        {formatFileSize(source.totalFileBytes)}
                      </div>
                      <div className="company-ai-source-strip company-ai-source-strip--spaced">
                        <span className="company-ai-source-pill">{source.scopeType || 'company'}</span>
                        {source.scopeRef ? <span className="company-ai-source-pill">scope: {source.scopeRef}</span> : null}
                        {!canUseSimplifiedKnowledgeEditor(source) ? <span className="company-ai-source-pill">legacy flow</span> : null}
                      </div>
                      <div className="company-ai-inline-actions company-ai-inline-actions--spaced">
                        <Button type="button" size="sm" variant="secondary" onClick={() => void handleEditSource(source)}>
                          Edit Details
                        </Button>
                        <Button type="button" size="sm" variant="secondary" loading={reindexingSourceId === source.aiKnowledgeSourceId} onClick={() => void handleReindexSource(source.aiKnowledgeSourceId)}>
                          {getKnowledgeIndexActionLabel(source)}
                        </Button>
                        <Button type="button" size="sm" variant="secondary" onClick={() => void handleOpenSource(source)}>
                          Open
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="🗃️"
                  title="No knowledge sources registered"
                  description="Add the company reference library or upload a file to start building the retrievable AI knowledge layer."
                  className="company-ai-admin-empty-state"
                />
              )}
            </Card>
            ) : null}

            {activeAdminTab === 'knowledge' ? (
            <Card
              header={(
                <CardHeader
                  title="Structured Knowledge Documents"
                  subtitle="These are the normalized document records created by the structured PDF pipeline."
                  actions={<StatusBadge status={String(filteredKnowledgeDocuments.length)}>{filteredKnowledgeDocuments.length} docs</StatusBadge>}
                />
              )}
            >
              <div className="company-ai-stacked">
                <SearchInput
                  label="Filter documents"
                  value={knowledgeDocumentFilter}
                  onChange={(event) => setKnowledgeDocumentFilter(event.target.value)}
                  placeholder="Title, type, domain, or path"
                  fullWidth
                />

                {filteredKnowledgeDocuments.length ? (
                  <div className="company-ai-list company-ai-list--inventory">
                    {filteredKnowledgeDocuments.map((document) => (
                      <button
                        key={document.knowledgeDocumentId}
                        type="button"
                        className={`company-ai-list-button${selectedKnowledgeDocumentId === document.knowledgeDocumentId ? ' company-ai-list-button--active' : ''}`}
                        onClick={() => void handleSelectKnowledgeDocument(document.knowledgeDocumentId)}
                      >
                        <div className="company-ai-list-title">{document.title}</div>
                        <div className="company-ai-list-subtitle">
                          {document.documentType || 'unknown type'}
                          {' · '}
                          {document.businessDomain || 'unknown domain'}
                          {' · '}
                          {document.totalPages} pages
                        </div>
                        <div className="company-ai-muted company-ai-muted--spaced">
                          {document.sourceFilePath || document.fileName || 'No source path'}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="📄"
                    title="No structured knowledge documents yet"
                    description="Run structured PDF ingestion for a company knowledge source and the normalized document list will appear here."
                    className="company-ai-admin-empty-state"
                  />
                )}
              </div>
            </Card>
            ) : null}
          </div>
        </div>
      </PageSection>

      {loading ? (
        <PageSection width="wide" padding="compact" className="company-ai-admin-section company-ai-admin-section--loading">
          <div className="company-ai-muted">Loading AI admin data…</div>
        </PageSection>
      ) : null}
    </div>
  );
}
